import { useQuery } from '@tanstack/react-query';
import type {
  DeliveryNoteDTO,
  PriceListDTO,
  WholesaleCustomerDTO,
  WholesaleOrderStatus,
  WholesaleSettingsDTO,
  WholesaleStaffDTO,
  WholesaleTagDTO,
  WholesaleVehicleDTO,
} from '@bifeai/shared';
import { Button, type Tone } from '@bifeai/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { printDeliveryNotes } from '../../lib/delivery-note-print';

export const STATUS_TONES: Record<WholesaleOrderStatus, Tone> = {
  PENDING: 'info',
  PREPARING: 'warning',
  READY: 'success',
  ON_ROUTE: 'info',
  DELIVERED: 'neutral',
  SETTLED: 'neutral',
  CANCELED: 'danger',
};

export const useWsSettings = () =>
  useQuery({ queryKey: ['wholesale', 'settings'], queryFn: () => api.get<WholesaleSettingsDTO>('/wholesale/settings') });
export const useStaff = () => useQuery({ queryKey: ['wholesale', 'staff'], queryFn: () => api.get<WholesaleStaffDTO[]>('/wholesale/staff') });
export const usePriceLists = () =>
  useQuery({ queryKey: ['wholesale', 'price-lists'], queryFn: () => api.get<PriceListDTO[]>('/wholesale/price-lists') });
export const useTags = (kind: 'segments' | 'zones') =>
  useQuery({ queryKey: ['wholesale', 'tags', kind], queryFn: () => api.get<WholesaleTagDTO[]>(`/wholesale/tags/${kind}`) });
export const useVehicles = () =>
  useQuery({ queryKey: ['wholesale', 'vehicles'], queryFn: () => api.get<WholesaleVehicleDTO[]>('/wholesale/vehicles') });
export const useWsCustomers = (search = '') =>
  useQuery({
    queryKey: ['wholesale', 'customers', search],
    queryFn: () => api.get<WholesaleCustomerDTO[]>(`/wholesale/customers${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  });

/** "$ 15.000,50" o "15000.5" → centavos. null si no es un número. */
export function parseMoney(v: string): number | null {
  const s = v.replace(/[^\d.,-]/g, '');
  if (!s) return null;
  const norm = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s.split('.').length > 2 ? s.replace(/\./g, '') : s;
  const n = Number(norm);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/** Centavos → texto para un campo ("15000,5"). */
export const moneyInput = (cents: number | null | undefined): string =>
  cents === null || cents === undefined ? '' : (cents / 100).toLocaleString('es-AR', { maximumFractionDigits: 2, useGrouping: false });

/** Kilos (con coma) o unidades → gramos o unidades. */
export function parseQty(unitType: 'WEIGHT' | 'UNIT', v: string): number | null {
  const n = Number(v.replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return null;
  return unitType === 'WEIGHT' ? Math.round(n * 1000) : Math.round(n);
}

export const qtyInput = (unitType: string, qty: number | null | undefined): string =>
  qty === null || qty === undefined ? '' : unitType === 'WEIGHT' ? String(qty / 1000).replace('.', ',') : String(qty);

export const qtyText = (unitType: string, qty: number): string =>
  unitType === 'WEIGHT' ? `${(qty / 1000).toLocaleString('es-AR', { maximumFractionDigits: 3 })} kg` : `${qty} u.`;

export const lineTotal = (unitType: string, qty: number, priceCents: number): number =>
  unitType === 'WEIGHT' ? Math.round((priceCents * qty) / 1000) : priceCents * qty;

/** Paginado en pantalla: "Anterior · 1 a 24 de 130 · Siguiente". No se muestra si entra todo. */
export function usePaged<T>(rows: T[], size: number, resetKey: string): { page: T[]; pager: ReactNode } {
  const [p, setP] = useState(1);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  useEffect(() => setP(1), [resetKey]);
  const cur = Math.min(p, pages);
  const page = rows.slice((cur - 1) * size, cur * size);
  const pager =
    rows.length > size ? (
      <div className="bf-ws-pager">
        <Button size="sm" disabled={cur <= 1} onClick={() => setP(cur - 1)}>
          Anterior
        </Button>
        <span className="bf-cell-sub">
          {(cur - 1) * size + 1} a {Math.min(cur * size, rows.length)} de {rows.length}
        </span>
        <Button size="sm" disabled={cur >= pages} onClick={() => setP(cur + 1)}>
          Siguiente
        </Button>
      </div>
    ) : null;
  return { page, pager };
}

/** Imprimir comprobantes de entrega (documento interno). Recuerda si se imprimen con o sin precios. */
export function PrintNotesButton({ orderIds, label = 'Imprimir comprobante de entrega' }: { orderIds: string[]; label?: string }) {
  const [prices, setPrices] = useState(() => {
    try {
      return window.localStorage.getItem('bifeai.ws.notePrices') !== '0';
    } catch {
      return true;
    }
  });
  const [busy, setBusy] = useState(false);
  if (!orderIds.length) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <Button
        size="sm"
        loading={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const notes = await Promise.all(orderIds.map((id) => api.get<DeliveryNoteDTO>(`/wholesale/orders/${id}/delivery-note`)));
            printDeliveryNotes(notes, prices);
          } finally {
            setBusy(false);
          }
        }}
      >
        {label}
      </Button>
      <label className="bf-cell-sub" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={prices}
          onChange={(e) => {
            setPrices(e.target.checked);
            try {
              window.localStorage.setItem('bifeai.ws.notePrices', e.target.checked ? '1' : '0');
            } catch {
              // sin lugar: solo por esta vez
            }
          }}
        />
        con precios
      </label>
    </span>
  );
}
